import { Button, Divider, Flex, Link, Text } from "@chakra-ui/react";
import BaseImage from "@components/BaseImage";
import LearnMore from "@components/BridgeWrap/LearnMoreAccordian";
import BridgeJourneySteps from "@components/BridgeWrap/StepsJourney";
import TokenSelect from "@components/BridgeWrap/TokenSelect";
import SanctionBanner from "@components/SanctionBanner";
import { CONNECT_WALLET } from "@constants/analytics";
import useBridgeWrapConfig from "@hooks/useBridgeWrapConfig";
import useLedgerCookies from "@hooks/useLedgerCookies";
import useSanction from "@hooks/useSanction";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import React, { useRef, useState } from "react";
import { trackEvent } from "utils";
import { useAccount } from "wagmi";
import AgEthJourneySteps from "./StepsJourneyAgeth";
import { BW_VARIANT, TBW_VARIANT, TChainSelection } from "./types";

type BridgeWrapProps = {
  variant: TBW_VARIANT;
};

const BridgeWrap: React.FC<BridgeWrapProps> = ({ variant }) => {
  useLedgerCookies();

  const agETHJourney = variant === BW_VARIANT.AGETH;

  const { isConnected, chain } = useAccount();
  const { openConnectModal } = useConnectModal();
  const { isSanctioned } = useSanction();

  const BRIDGE_WRAP_DATA = useBridgeWrapConfig(variant);

  const [sourceChain, setSourceChain] = useState<TChainSelection>();

  const [destinationChain, setDestinationChain] = useState<TChainSelection>();

  const accordionRef = useRef<{ openAccordion: () => void }>(null);

  const goToLearnMore = () => {
    accordionRef.current?.openAccordion();
  };

  const handleConnectWallet = () => {
    openConnectModal?.();
    trackEvent(CONNECT_WALLET, {
      type: "bridge_wrap_page",
    });
  };

  const handleOnChainSelection = (option: any) => {
    setSourceChain(option);
  };

  const handleDestinationChainSelection = (option: any) => {
    setDestinationChain(option);
  };

  const renderMessage = () => {
    if (
      sourceChain?.id &&
      destinationChain?.id &&
      (sourceChain.id === 1 || (!agETHJourney && sourceChain.id === 42161)) &&
      sourceChain.id === destinationChain.id
    ) {
      return (
        <Flex justifyContent={"center"} mt="12px">
          <Text fontSize={"12px"} fontWeight={400}>
            You do not need bridging or wrapping. Start exploring DeFi.
          </Text>
        </Flex>
      );
    }
  };
  const renderSteps = () => {
    const isChainsSelected = sourceChain?.id && destinationChain?.id;
    if (!isConnected && !chain) {
      return (
        <Flex direction={"column"} gap="16px" width={"100%"}>
          <Flex
            height={"32px"}
            justifyContent={"space-between"}
            alignItems={"center"}
            width={"100%"}
          >
            <Text fontSize={"12px"} fontWeight={400}>
              Connect your wallet to fetch tokens & view steps.
            </Text>
            <Button
              borderRadius="6px"
              bg="textPrimary"
              p="4px 23px"
              h="32px"
              minW="126px"
              maxW="156px"
              _hover={{
                bg: "textPrimary",
              }}
              onClick={handleConnectWallet}
            >
              <Text
                fontSize={"10px"}
                lineHeight={"16px"}
                fontWeight={"700"}
                color="backgroundCard"
              >
                Connect Wallet
              </Text>
            </Button>
          </Flex>
          <Divider my={2} bg={"#B3CECE"} height="1px" />
        </Flex>
      );
    }

    if (!isChainsSelected) {
      return (
        <Flex direction={"column"} gap="16px" width={"100%"}>
          <Text fontSize={"12px"} fontWeight={400}>
            Select a source and destination to view steps
          </Text>
          <Divider my={2} bg={"#B3CECE"} height="1px" />
        </Flex>
      );
    }

    if (sourceChain.id === -1) {
      return (
        <Text fontSize={"12px"} fontWeight={400}>
          Please selected a supported source chain
        </Text>
      );
    }

    return (
      sourceChain.id &&
      destinationChain.id &&
      (agETHJourney ? (
        <AgEthJourneySteps
          sourceChain={sourceChain}
          destinationChain={destinationChain}
        />
      ) : (
        <BridgeJourneySteps
          sourceChain={sourceChain}
          destinationChain={destinationChain}
        />
      ))
    );
  };

  return (
    <>
      <Flex
        justifyContent={"center"}
        p={{ base: "0 16px", sm: "0 20px", md: 0 }}
      >
        <Flex
          flexDir="column"
          gap="12px"
          mt={{ base: "16px", xl: "0px" }}
          w={{ base: "100%", md: "500px" }}
          mb="12px"
        >
          {isSanctioned && <SanctionBanner />}
        </Flex>
      </Flex>
      <Flex
        justifyContent={"center"}
        p={{ base: "0 16px", sm: "0 20px", md: 0 }}
        pointerEvents={isSanctioned ? "none" : "auto"}
        opacity={isSanctioned ? 0.5 : 1}
      >
        <Flex
          flexDir="column"
          gap="12px"
          mt={{ base: "16px", xl: "0px" }}
          w={{ base: "100%", md: "500px" }}
        >
          <Flex flexDir={"column"} gap="8px">
            <Text fontSize={"32px"} lineHeight={"38px"} fontWeight={600}>
              Bridge & Wrap {variant}
            </Text>
            <Text fontSize={"14px"} lineHeight={"21px"} fontWeight={600}>
              Move your tokens across chains, without losing usability.
            </Text>
          </Flex>

          <Flex
            bg="backgroundCard"
            p={6}
            borderRadius="24px"
            direction={"column"}
          >
            <Flex
              justifyContent={"space-between"}
              gap="10px"
              direction={{ base: "column", sm: "row" }}
            >
              <Flex position={"relative"} width={{ base: "100%", md: "230px" }}>
                <TokenSelect
                  key="sourceChain"
                  sectionLabel="Source"
                  placeholder="Select source"
                  options={BRIDGE_WRAP_DATA.sourceChains}
                  selectedOption={sourceChain}
                  onSelection={handleOnChainSelection}
                />
              </Flex>
              <Flex
                position={"relative"}
                width={{ base: "100%", md: "230px" }}
                ml={{ base: 0, sm: "10px" }}
              >
                <TokenSelect
                  key="destinationChain"
                  sectionLabel="Destination"
                  placeholder="Select destination"
                  options={BRIDGE_WRAP_DATA.destinationChains}
                  selectedOption={destinationChain}
                  onSelection={handleDestinationChainSelection}
                />
              </Flex>
            </Flex>
            <Flex bg={"#F4F4F4"} p={3} borderRadius={"10px"} gap="5px">
              <Flex alignItems={"start"}>
                <BaseImage
                  src="/assets/info-tips.svg"
                  alt=""
                  height={24}
                  width={24}
                />
              </Flex>
              <Flex>
                <Text fontSize={"12px"} fontWeight={400} color={"#2D2C2C"}>
                  Keep source & destination chain the same if you only want to
                  wrap an asset.{" "}
                  <Link isExternal onClick={() => goToLearnMore()}>
                    <u>Learn More</u>
                  </Link>{" "}
                  about wrapped assets.
                </Text>
              </Flex>
            </Flex>

            {renderMessage()}
          </Flex>
          <Divider my={2} bg={"#B3CECE"} height="1px" />

          <Flex>{renderSteps()}</Flex>

          <Flex
            flexDir="column"
            gap={{ base: "16px", md: "24px" }}
            width={"100%"}
          >
            <LearnMore variant={variant} ref={accordionRef} />
          </Flex>
        </Flex>
      </Flex>
    </>
  );
};

export default BridgeWrap;
